import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Label } from "../components/ui/label";
import { ArrowLeft, ArrowRight, Loader2, Brain, Heart, Activity, User, Check } from "lucide-react";

const categoryIcons = {
  emotions: Heart,
  thoughts: Brain,
  behavior: Activity,
  physical: User
};

const categoryColors = {
  emotions: "text-pink-400",
  thoughts: "text-indigo-400",
  behavior: "text-amber-400",
  physical: "text-green-400"
};

export default function MayaManthanAssessment() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [questions, setQuestions] = useState({});
  const [currentCategory, setCurrentCategory] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const categories = ["emotions", "thoughts", "behavior", "physical"];

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      const response = await axios.get(`${API}/maya-manthan/assessment/questions`);
      setQuestions(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching questions:", error);
      toast.error("Failed to load assessment");
      setLoading(false);
    }
  };

  const currentCategoryKey = categories[currentCategory];
  const currentQuestions = questions[currentCategoryKey] || [];
  const currentQ = currentQuestions[currentQuestion];

  const totalQuestions = categories.reduce((sum, cat) => sum + (questions[cat]?.length || 0), 0);
  const answeredQuestions = Object.keys(responses).length;
  const progress = totalQuestions > 0 ? (answeredQuestions / totalQuestions) * 100 : 0;

  const handleAnswer = (answer, answerIndex) => {
    if (!currentQ) return;

    setResponses(prev => ({
      ...prev,
      [currentQ.id]: {
        question_id: currentQ.id,
        question_text: currentQ.text,
        answer: answer,
        answer_index: answerIndex,
        category: currentCategoryKey
      }
    }));
  };

  const goNext = () => {
    if (currentQuestion < currentQuestions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else if (currentCategory < categories.length - 1) {
      setCurrentCategory(prev => prev + 1);
      setCurrentQuestion(0);
    }
  };

  const goPrev = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    } else if (currentCategory > 0) {
      setCurrentCategory(prev => prev - 1);
      const prevCatQuestions = questions[categories[currentCategory - 1]] || [];
      setCurrentQuestion(prevCatQuestions.length - 1);
    }
  };

  const isLastQuestion = currentCategory === categories.length - 1 && 
                         currentQuestion === currentQuestions.length - 1;
  const isFirstQuestion = currentCategory === 0 && currentQuestion === 0;
  const hasCurrentAnswer = currentQ && responses[currentQ.id];

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/maya-manthan/assessment/submit`, {
        responses: Object.values(responses)
      });
      setResult(response.data);
      toast.success("Assessment complete!");
    } catch (error) {
      console.error("Submit error:", error);
      toast.error("Failed to submit assessment");
    } finally {
      setSubmitting(false);
    }
  };

  const Icon = categoryIcons[currentCategoryKey] || Brain;
  const iconColor = categoryColors[currentCategoryKey] || "text-white";

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a1a] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  // Show results if available
  if (result) {
    return <AssessmentResult result={result} navigate={navigate} />;
  }

  return (
    <div className="min-h-screen bg-[#0a0a1a] relative overflow-hidden" data-testid="maya-assessment">
      <div className="fixed inset-0 bg-gradient-to-b from-[#0d0d2b] via-[#0a0a1a] to-[#050510]" />

      {/* Progress Bar */}
      <div className="fixed top-0 left-0 right-0 h-1 bg-white/10 z-50">
        <div 
          className="h-full bg-gradient-to-r from-amber-500 to-pink-500 transition-all duration-500"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Back Button */}
      <button
        onClick={() => navigate('/maya-manthan')}
        className="fixed top-6 left-6 z-50 p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
      >
        <ArrowLeft className="w-5 h-5 text-gray-400" />
      </button>

      {/* Step Indicator */}
      <div className="fixed top-6 right-6 z-50 flex items-center gap-2">
        {categories.map((cat, index) => {
          const CatIcon = categoryIcons[cat];
          return (
            <div
              key={cat}
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                index < currentCategory ? 'bg-green-500/20 text-green-400' :
                index === currentCategory ? 'bg-white/10 text-white' : 'bg-white/5 text-gray-600'
              }`}
            >
              <CatIcon className="w-5 h-5" />
            </div>
          );
        })}
      </div>

      {/* Main Content */}
      <div className="relative min-h-screen flex items-center justify-center px-6 py-24">
        <div className="w-full max-w-2xl">
          {/* Category Label */}
          <motion.div
            key={currentCategoryKey}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 ${iconColor}`}>
              <Icon className="w-4 h-4" />
              <span className="text-sm capitalize">{currentCategoryKey}</span>
            </div>
          </motion.div>

          {/* Question Card */}
          <AnimatePresence mode="wait">
            {currentQ && (
              <motion.div
                key={currentQ.id}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.4 }}
                className="bg-[#0d0d2b]/60 backdrop-blur-xl border border-white/5 rounded-2xl p-8 md:p-12"
              >
                <h2 className="font-serif text-2xl md:text-3xl text-white mb-8 text-center">
                  {currentQ.text}
                </h2>

                <RadioGroup
                  value={responses[currentQ.id]?.answer || ""}
                  onValueChange={(value) => {
                    const index = currentQ.options.indexOf(value);
                    handleAnswer(value, index);
                  }}
                  className="space-y-4"
                >
                  {currentQ.options.map((option, index) => (
                    <motion.div
                      key={option}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <Label
                        htmlFor={`option-${index}`}
                        className={`flex items-center p-4 rounded-xl cursor-pointer transition-all border ${
                          responses[currentQ.id]?.answer === option
                            ? 'bg-amber-500/10 border-amber-500/50'
                            : 'bg-white/5 border-white/10 hover:bg-white/10'
                        }`}
                      >
                        <RadioGroupItem 
                          value={option} 
                          id={`option-${index}`}
                          className="border-gray-500 text-amber-500"
                        />
                        <span className="ml-3 text-white">{option}</span>
                      </Label>
                    </motion.div>
                  ))}
                </RadioGroup>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8">
            <Button
              onClick={goPrev}
              disabled={isFirstQuestion}
              variant="ghost"
              className="text-gray-400 hover:text-white disabled:opacity-30"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Previous
            </Button>

            {isLastQuestion ? (
              <Button
                onClick={handleSubmit}
                disabled={!hasCurrentAnswer || submitting}
                className="bg-amber-500 hover:bg-amber-600 text-black"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    Complete Assessment
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            ) : (
              <Button
                onClick={goNext}
                disabled={!hasCurrentAnswer}
                className="bg-white/10 hover:bg-white/20 text-white"
              >
                Next
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            )}
          </div>

          {/* Question Counter */}
          <div className="text-center mt-8">
            <span className="text-gray-500 text-sm">
              Question {answeredQuestions + 1} of {totalQuestions}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function AssessmentResult({ result, navigate }) {
  const { profile, analysis, personalized_plan } = result;

  const chakraColors = {
    root: "#FF3B30",
    sacral: "#FF9500",
    solar_plexus: "#FFCC00",
    heart: "#34C759",
    throat: "#30B0C7",
    third_eye: "#5856D6",
    crown: "#AF52DE"
  };

  const chakraNames = {
    root: "Root",
    sacral: "Sacral",
    solar_plexus: "Solar Plexus",
    heart: "Heart",
    throat: "Throat",
    third_eye: "Third Eye",
    crown: "Crown"
  };

  const gunaDescriptions = {
    sattva: "Pure, harmonious energy characterized by clarity, peace, and wisdom.",
    rajas: "Active, passionate energy characterized by desire, ambition, and restlessness.",
    tamas: "Heavy, inert energy characterized by darkness, lethargy, and resistance."
  };

  // Generate structured insights
  const generateCurrentState = () => {
    const blockedChakras = Object.entries(analysis.chakra_status)
      .filter(([_, status]) => status === 'blocked' || status === 'weak')
      .map(([chakra]) => chakraNames[chakra]);
    
    if (blockedChakras.length > 0) {
      return `Your ${analysis.dominant_guna} nature is currently experiencing blocks in the ${blockedChakras.slice(0, 2).join(' and ')} ${blockedChakras.length > 1 ? 'chakras' : 'chakra'}. Your mind type is ${analysis.mind_type}, which means you process experiences through ${analysis.mind_type === 'analytical' ? 'logical analysis' : analysis.mind_type === 'emotional' ? 'feeling and intuition' : 'balanced awareness'}.`;
    }
    return `Your energy system shows a predominant ${analysis.dominant_guna} quality with your ${analysis.mind_type} mind type guiding your inner journey.`;
  };

  const generateWhyItHappens = () => {
    return analysis.root_cause || `When ${analysis.dominant_guna} energy becomes imbalanced, it often manifests as ${analysis.dominant_guna === 'rajas' ? 'restlessness, overthinking, and difficulty finding peace' : analysis.dominant_guna === 'tamas' ? 'lethargy, resistance to change, and emotional heaviness' : 'disconnection from grounded daily life despite spiritual aspirations'}. This pattern typically develops through accumulated stress, unprocessed emotions, and lifestyle factors that don't support your natural rhythm.`;
  };

  const generateWhatToDo = () => {
    const practices = [];
    if (personalized_plan?.morning) {
      practices.push(`Begin each morning with ${personalized_plan.morning.practice} (${personalized_plan.morning.duration} minutes)`);
    }
    if (personalized_plan?.afternoon) {
      practices.push(`Midday, practice ${personalized_plan.afternoon.practice} to maintain balance`);
    }
    if (personalized_plan?.evening) {
      practices.push(`End your day with ${personalized_plan.evening.practice} for integration`);
    }
    return practices.length > 0 ? practices : [
      "Practice grounding meditation for 10 minutes each morning",
      "Use Diya Tratak (ghee lamp gazing) to strengthen focus",
      "Journal your inner experiences before sleep"
    ];
  };

  return (
    <div className="min-h-screen bg-[#0a0a1a] relative" data-testid="assessment-result">
      <div className="fixed inset-0 bg-gradient-to-b from-[#0d0d2b] via-[#0a0a1a] to-[#050510]" />
      
      {/* Ambient glow */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-purple-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 py-12">
        {/* Header with spiritual greeting */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/20 mb-6">
            <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
            <span className="text-amber-400 text-sm">Assessment Complete</span>
          </div>
          <h1 className="font-serif text-4xl md:text-5xl text-white mb-4">Your Inner Truth Revealed</h1>
          <p className="text-gray-400 text-lg">A mirror to your energy, mind, and spirit</p>
        </motion.div>

        {/* Section 1: Current State */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-br from-[#0d0d2b]/80 to-[#1a1a3a]/60 backdrop-blur-xl border border-white/10 rounded-2xl p-8 mb-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
              <span className="text-amber-400 text-lg">1</span>
            </div>
            <div>
              <h2 className="font-serif text-2xl text-white">Current State</h2>
              <p className="text-gray-500 text-sm">Where you are right now</p>
            </div>
          </div>
          
          <p className="text-gray-300 leading-relaxed text-lg mb-6">{generateCurrentState()}</p>
          
          {/* Chakra Visual */}
          <div className="grid grid-cols-4 md:grid-cols-7 gap-3 mt-6">
            {Object.entries(analysis.chakra_status).map(([chakra, status]) => (
              <div key={chakra} className="text-center">
                <div 
                  className="w-10 h-10 md:w-12 md:h-12 rounded-full mx-auto mb-2 transition-all duration-300"
                  style={{ 
                    backgroundColor: chakraColors[chakra],
                    opacity: status === 'blocked' ? 0.3 : status === 'weak' ? 0.6 : 1,
                    boxShadow: status === 'balanced' ? `0 0 20px ${chakraColors[chakra]}80` : 'none'
                  }}
                />
                <p className="text-white text-xs">{chakraNames[chakra]}</p>
                <p className={`text-xs capitalize ${
                  status === 'balanced' ? 'text-green-400' :
                  status === 'weak' ? 'text-yellow-400' : 'text-red-400'
                }`}>{status}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Section 2: Why It Happens */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-br from-indigo-900/30 to-purple-900/20 border border-indigo-500/20 rounded-2xl p-8 mb-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-indigo-500/20 flex items-center justify-center">
              <span className="text-indigo-400 text-lg">2</span>
            </div>
            <div>
              <h2 className="font-serif text-2xl text-white">Why It Happens</h2>
              <p className="text-gray-500 text-sm">The root of your patterns</p>
            </div>
          </div>
          
          <p className="text-gray-300 leading-relaxed text-lg">{generateWhyItHappens()}</p>
          
          {/* Guna Bars */}
          <div className="mt-6 flex gap-4">
            {Object.entries(analysis.guna_scores).map(([guna, score]) => (
              <div key={guna} className="flex-1">
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-gray-500 capitalize">{guna}</span>
                  <span className="text-white">{score}%</span>
                </div>
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <div 
                    className={`h-full transition-all duration-500 ${
                      guna === 'sattva' ? 'bg-green-500' :
                      guna === 'rajas' ? 'bg-amber-500' : 'bg-gray-500'
                    }`}
                    style={{ width: `${score}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Section 3: What To Do Next */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-gradient-to-br from-emerald-900/20 to-teal-900/10 border border-emerald-500/20 rounded-2xl p-8 mb-8"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <span className="text-emerald-400 text-lg">3</span>
            </div>
            <div>
              <h2 className="font-serif text-2xl text-white">What To Do Next</h2>
              <p className="text-gray-500 text-sm">Your path to transformation</p>
            </div>
          </div>
          
          <div className="space-y-4">
            {generateWhatToDo().map((practice, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-4 h-4 text-emerald-400" />
                </div>
                <p className="text-gray-300">{practice}</p>
              </div>
            ))}
          </div>
          
          {/* Personalized Plan Cards */}
          {personalized_plan && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
              {Object.entries(personalized_plan).map(([time, plan]) => (
                <div key={time} className="bg-white/5 rounded-xl p-4 border border-white/5">
                  <p className="text-amber-400 text-sm capitalize mb-2">{time}</p>
                  <p className="text-white font-medium">{plan.practice}</p>
                  <p className="text-gray-500 text-xs mt-1">{plan.duration} min • {plan.focus}</p>
                </div>
              ))}
            </div>
          )}
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-gradient-to-r from-amber-500/20 to-amber-600/10 border border-amber-500/30 rounded-2xl p-8 text-center"
        >
          <h3 className="font-serif text-2xl text-white mb-3">
            Ready to Transform?
          </h3>
          <p className="text-gray-400 mb-6 max-w-xl mx-auto">
            Your 21-day journey awaits. Commit to daily practice and watch your inner world shift.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate('/maya-manthan')}
              className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-medium px-8 py-3 rounded-full shadow-lg shadow-amber-500/20"
              data-testid="begin-practice-btn"
            >
              Begin My 21-Day Practice
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              onClick={() => navigate('/maya-manthan/courses')}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10 px-8 py-3 rounded-full"
            >
              View Guided Programs
            </Button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
